import type { JSX } from 'react'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import type { ServerDto, ServerProbeDto } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { useServers } from '@/hooks/queries'
import { useCanOperate } from '@/stores/session'
import { DEFAULT_TOAST_MS, UNDO_TOAST_MS, useToastStore } from '@/stores/toasts'
import { ConfirmDestructive } from '@/components/ConfirmDestructive'
import { Button } from '@/ui/primitives/Button'
import { SectionHead } from '@/ui/primitives/Surface'
import { EmptyBlock, ErrorBlock, SkeletonGrid } from '../shared/states'
import { problemText } from '../shared/problem'
import { ServerDialog } from './ServerDialog'
import { ServerTile } from './ServerTile'

/**
 * Экран профилей серверов.
 *
 * Профиль — это ответ на «куда заходит бот». Значение, вокруг которого построен
 * экран, — не адрес, а проба: поддерживает ли ядро версию этого сервера. Без неё
 * бот создаётся вслепую и падает уже в бою.
 */

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3'

/**
 * Сколько панель ждёт ответ на пинг.
 *
 * У `fetch` своего таймаута нет вообще: если запрос завис (упавший upstream,
 * заблокированный event loop на API, оборванный туннель), промис не отвергается
 * минутами, а кнопка всё это время «крутится». Именно так баг и выглядел
 * снаружи. Сам SLP-пинг на сервере ограничен 3 с (`SLP_TIMEOUT_MS`), плюс
 * очередь и сеть — 20 с с запасом покрывают честный ответ, а всё, что дольше,
 * это уже поломка, о которой надо сказать вслух.
 */
const PING_TIMEOUT_MS = 20_000

/** Итог последнего пинга конкретного профиля: свежая проба либо текст ошибки. */
type PingOutcome = { probe: ServerProbeDto; error?: never } | { probe?: never; error: string }

/**
 * Пинг с дедлайном. Возвращает «человеческую» ошибку вместо `AbortError`:
 * пользователю нужно знать, что делать, а не как называется исключение.
 */
async function pingWithDeadline(id: string): Promise<ServerProbeDto> {
  const controller = new AbortController()
  let expired = false
  const timer = setTimeout(() => {
    expired = true
    controller.abort()
  }, PING_TIMEOUT_MS)

  try {
    return await api.servers.ping(id, controller.signal)
  } catch (error) {
    if (expired) {
      throw new Error(
        `Панель не дождалась ответа за ${Math.round(PING_TIMEOUT_MS / 1000)} с. Похоже, отвечает не сервер Minecraft, а сама панель: проверьте, что API жив, и повторите.`
      )
    }
    throw error
  } finally {
    clearTimeout(timer)
  }
}

export function ServersScreen(): JSX.Element {
  const queryClient = useQueryClient()
  const push = useToastStore(state => state.push)

  const servers = useServers()
  const canOperate = useCanOperate()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<ServerDto | null>(null)
  const [toDelete, setToDelete] = useState<ServerDto | null>(null)
  /**
   * Результаты пингов этой сессии, по id профиля.
   *
   * Зачем держать их у себя, а не полагаться на рефетч списка. Во-первых,
   * `latencyMs` в списке всегда `null`: задержку негде хранить, в профиле её
   * колонки нет (`ServersService.toDto`), — а «сколько миллисекунд» это половина
   * смысла кнопки. Во-вторых, ответ мутации приходит раньше рефетча, и карточка
   * обязана перестать говорить «Пинга ещё не было» сразу, а не когда-нибудь.
   */
  const [outcomes, setOutcomes] = useState<Record<string, PingOutcome>>({})

  const ping = useMutation({
    mutationFn: (server: ServerDto) => pingWithDeadline(server.id),
    // Прошлый результат снимаем на старте: висящая ошибка рядом с крутящейся
    // кнопкой читается как «ошибка прямо сейчас».
    onMutate: (server: ServerDto) => {
      setOutcomes(previous => {
        const { [server.id]: _dropped, ...rest } = previous
        return rest
      })
    },
    onSuccess: (probe, server) => {
      setOutcomes(previous => ({ ...previous, [server.id]: { probe } }))
      // Список всё равно обновляем: тот же профиль виден на других экранах, и
      // `pingedAt` из БД должен доехать до них.
      void queryClient.invalidateQueries({ queryKey: qk.servers.list })
      push({
        message: probe.online
          ? `${server.name}: онлайн, ${probe.version ?? 'версия неизвестна'}${probe.supportedByCore ? '' : ' — ядро эту версию не знает'}`
          : `${server.name}: не отвечает`,
        tone: probe.online && probe.supportedByCore ? 'success' : 'warning',
        timeoutMs: DEFAULT_TOAST_MS
      })
    },
    /**
     * Ошибка живёт в карточке, а не в тосте: тост уезжает через несколько
     * секунд, а вопрос «почему не пингуется» остаётся. Рядом с кнопкой видно и
     * причину, и то, что повторить можно тут же.
     */
    onError: (error, server) => {
      setOutcomes(previous => ({ ...previous, [server.id]: { error: problemText(error) } }))
    }
  })

  const remove = useMutation({
    mutationFn: (server: ServerDto) => api.servers.remove(server.id),
    onSuccess: (_data, server) => {
      void queryClient.invalidateQueries({ queryKey: qk.servers.list })
      push({
        message: `Профиль «${server.name}» удалён`,
        tone: 'warning',
        timeoutMs: UNDO_TOAST_MS
      })
    },
    onError: error =>
      push({ message: problemText(error), tone: 'danger', timeoutMs: DEFAULT_TOAST_MS })
  })

  const openCreate = (): void => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (server: ServerDto): void => {
    setEditing(server)
    setDialogOpen(true)
  }

  const items = servers.data?.items ?? []

  return (
    <>
      {/* Без заголовка: имя страницы уже стоит в хедере каркаса, второй такой же
          заголовок — дубль. Остаётся только строка действий. */}
      <SectionHead
        actions={
          <Button
            variant="primary"
            disabled={!canOperate}
            title={canOperate ? undefined : 'Недостаточно прав'}
            onClick={openCreate}
          >
            <Plus aria-hidden className="size-4" />
            Добавить сервер
          </Button>
        }
      />

      {servers.isPending ? (
        <SkeletonGrid count={2} className={GRID} tileClassName="h-60" />
      ) : servers.isError ? (
        <ErrorBlock error={servers.error} onRetry={() => void servers.refetch()} />
      ) : items.length === 0 ? (
        <EmptyBlock
          title="Профилей серверов пока нет"
          note="Профиль хранит адрес, версию, команду после входа и хуки — реакции на строки чата вроде «5 минут до рестарта». Один профиль обслуживает сколько угодно ботов."
          action={{
            label: 'Добавить сервер',
            onClick: openCreate,
            disabled: !canOperate,
            ...(canOperate ? {} : { title: 'Недостаточно прав' })
          }}
        />
      ) : (
        <div className={GRID}>
          {items.map((server, index) => (
            <div
              key={server.id}
              className="anim-rise"
              style={{ '--i': Math.min(index, 12) } as never}
            >
              <ServerTile
                server={server}
                canOperate={canOperate}
                pinging={ping.isPending && ping.variables?.id === server.id}
                freshProbe={outcomes[server.id]?.probe ?? null}
                pingError={outcomes[server.id]?.error ?? null}
                onPing={() => ping.mutate(server)}
                onEdit={() => openEdit(server)}
                onDelete={() => setToDelete(server)}
              />
            </div>
          ))}
        </div>
      )}

      <ServerDialog open={dialogOpen} server={editing} onClose={() => setDialogOpen(false)} />

      <ConfirmDestructive
        open={toDelete !== null}
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          if (toDelete === null) return
          remove.mutate(toDelete)
          setToDelete(null)
        }}
        title={`Удалить профиль «${toDelete?.name ?? ''}»?`}
        body="Профиль и его хуки будут удалены. Боты, привязанные к нему, останутся — им придётся выбрать другой сервер."
        expected={toDelete?.name ?? ''}
        loading={remove.isPending}
      />
    </>
  )
}
